/**
 * CampaignDetailsModal Component
 * 
 * Show real-time progress of an in-progress campaign
 */

import React, { useEffect, useState } from 'react';
import { X, Pause, Play, Square, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { pauseCampaign as pauseCampaignApi, resumeCampaign as resumeCampaignApi, cancelCampaign as cancelCampaignApi } from '../../api/outboundApi';

const CampaignDetailsModal = ({ campaign, isOpen, onClose, onUpdate }) => {
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [isPausing, setIsPausing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  // Auto-refresh last updated timestamp
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setLastUpdated(Date.now());
    }, 2000);

    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen || !campaign) return null;

  const handlePause = async () => {
    if (!window.confirm('Are you sure you want to pause this campaign?')) return;

    setIsPausing(true);
    try {
      const campaignId = campaign.itemId || campaign.campaignId;
      await pauseCampaignApi(campaignId);
      alert('Campaign paused successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error pausing campaign:', error);
      alert(error.message || 'Failed to pause campaign');
    } finally {
      setIsPausing(false);
    }
  };

  const handleResume = async () => {
    setIsResuming(true);
    try {
      const campaignId = campaign.itemId || campaign.campaignId;
      await resumeCampaignApi(campaignId);
      alert('Campaign resumed successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error resuming campaign:', error);
      alert(error.message || 'Failed to resume campaign');
    } finally {
      setIsResuming(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this campaign? This cannot be undone.')) return;

    setIsCancelling(true);
    try {
      const campaignId = campaign.itemId || campaign.campaignId;
      await cancelCampaignApi(campaignId);
      alert('Campaign cancelled successfully!');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error cancelling campaign:', error);
      alert(error.message || 'Failed to cancel campaign');
    } finally {
      setIsCancelling(false);
    }
  };

  const getTimeSince = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
    }
    
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    return `${diffHours}h ${remainingMins}m`;
  };

  const getEstimatedCompletion = () => {
    const elapsed = Date.now() - new Date(campaign.startedAt).getTime();
    const completed = campaign.completedCalls;
    const remaining = campaign.totalAppointments - completed;
    
    if (completed === 0) return 'Calculating...';
    
    const avgTimePerCall = elapsed / completed;
    const estimatedTimeRemaining = avgTimePerCall * remaining;
    const estimatedCompletionTime = new Date(Date.now() + estimatedTimeRemaining);
    
    const mins = Math.ceil(estimatedTimeRemaining / 60000);
    return `${estimatedCompletionTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} (~${mins} mins)`;
  };

  const getCallsPerMinute = () => {
    const elapsed = (Date.now() - new Date(campaign.startedAt).getTime()) / 60000; // minutes
    if (elapsed < 1) return '—';
    return Math.round(campaign.completedCalls / elapsed);
  };

  const progressPercentage = campaign.progress || Math.round((campaign.completedCalls / campaign.totalAppointments) * 100);

  // Calculate stats from csvData
  const calculateStats = () => {
    if (!campaign.csvData || !Array.isArray(campaign.csvData)) {
      return { answered: 0, voicemail: 0, noAnswer: 0 };
    }

    const completed = campaign.csvData.filter(row => row.callStatus === 'completed');
    const answered = completed.filter(row => row.callOutcome === 'answered').length;
    const voicemail = completed.filter(row => row.callOutcome === 'voicemail').length;
    const noAnswer = completed.filter(row => row.callOutcome === 'no_answer').length;

    return { answered, voicemail, noAnswer };
  };

  const stats = calculateStats();
  const secondsSinceUpdate = Math.floor((lastUpdated - Date.now()) / 1000) * -1;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-medium text-white">{campaign.name}</h2>
            <p className="text-sm text-gray-400 mt-1">
              {campaign.status === 'paused' ? '⏸ Campaign Paused' : '🟡 Campaign In Progress'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Progress Bar */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Progress</span>
                <span className="text-sm font-medium text-white">{progressPercentage}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-2xl font-bold text-white">{campaign.completedCalls}</span>
                <span className="text-sm text-gray-400">of {campaign.totalAppointments} calls</span>
              </div>
            </div>

            {/* Time Info */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-700">
              <div>
                <p className="text-xs text-gray-400 mb-1">Started</p>
                <p className="text-white font-medium">{getTimeSince(campaign.startedAt)} ago</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Est. Completion</p>
                <p className="text-white font-medium">{getEstimatedCompletion()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Calls/Minute</p>
                <p className="text-white font-medium">~{getCallsPerMinute()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Remaining</p>
                <p className="text-white font-medium">{campaign.totalAppointments - campaign.completedCalls} calls</p>
              </div>
            </div>

            {/* Last Updated */}
            <div className="flex items-center justify-center mt-4 pt-4 border-t border-gray-700">
              <RefreshCw className="w-3 h-3 text-gray-400 mr-2" />
              <p className="text-xs text-gray-400">
                Last updated: {secondsSinceUpdate} second{secondsSinceUpdate !== 1 ? 's' : ''} ago
              </p>
            </div>
          </div>

          {/* Current Stats */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h4 className="text-white font-medium mb-4">Current Stats</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Completed:</span>
                <span className="text-white font-medium">{campaign.completedCalls}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Remaining:</span>
                <span className="text-white font-medium">{campaign.totalAppointments - campaign.completedCalls}</span>
              </div>
              <div className="border-t border-gray-700 my-2"></div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Answered:</span>
                <span className="text-green-400 font-medium">
                  {stats.answered} ({Math.round((stats.answered / campaign.completedCalls) * 100) || 0}%)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Voicemail:</span>
                <span className="text-yellow-400 font-medium">
                  {stats.voicemail} ({Math.round((stats.voicemail / campaign.completedCalls) * 100) || 0}%)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">No Answer:</span>
                <span className="text-gray-400 font-medium">
                  {stats.noAnswer} ({Math.round((stats.noAnswer / campaign.completedCalls) * 100) || 0}%)
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {campaign.status === 'in_progress' && (
              <Button
                variant="outline"
                onClick={handlePause}
                disabled={isPausing || isCancelling}
                className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              >
                <Pause className="w-4 h-4 mr-2" />
                {isPausing ? 'Pausing...' : 'Pause Campaign'}
              </Button>
            )}
            
            {campaign.status === 'paused' && (
              <Button
                onClick={handleResume}
                disabled={isResuming || isCancelling}
                className="flex-1 bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white hover:opacity-90"
              >
                <Play className="w-4 h-4 mr-2" />
                {isResuming ? 'Resuming...' : 'Resume Campaign'}
              </Button>
            )}

            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isPausing || isCancelling || isResuming}
              className="flex-1 bg-red-900/20 border-red-700 text-red-400 hover:bg-red-900/40"
            >
              <Square className="w-4 h-4 mr-2" />
              {isCancelling ? 'Cancelling...' : 'Cancel Campaign'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetailsModal;

